import { useState } from "react";
import { Info, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { loadFeeModel, setFeeModel, useFeeModel } from "@/lib/fees";

const FIELDS = ["eea_pct", "eea_fixed", "uk_pct", "uk_fixed", "intl_pct", "intl_fixed", "fx_pct", "managed_pct", "billing_pct"];
const SRC = { default: "border-neutral-600 text-neutral-300", auto: "border-emerald-500/60 text-emerald-300", override: "border-amber-400/60 text-amber-200" };

const RateRow = ({ field, m }) => {
  const { t } = useI18n();
  const [val, setVal] = useState("");
  const save = async (value) => {
    if (value !== null && !(Number.isFinite(value) && value >= 0 && value <= 20)) {
      toast.error(t("fees.invalid"));
      return;
    }
    try {
      const { data } = await api.put("/admin/fees/override", { field, value });
      setFeeModel(data);
      setVal("");
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  const src = m.sources[field];
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-2 border-b border-neutral-800 py-2 text-sm" data-testid={`fee-rate-${field}`}>
      <span className="min-w-0 flex-1 basis-full break-words text-neutral-300 sm:basis-0">{t(`fees.f.${field}`)}</span>
      <span className="font-bold tabular-nums text-white" data-testid={`fee-rate-${field}-value`}>{m.rates[field]}</span>
      <span className={`rounded-full border px-1.5 text-[0.6rem] font-bold uppercase ${SRC[src]}`} data-testid={`fee-rate-${field}-src`}>{t(`fees.src.${src}`)}</span>
      <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal" placeholder={t("fees.override")} aria-label={t("fees.override")}
        className="w-20 min-w-0 rounded-lg border border-neutral-600 bg-black/40 px-2 py-1 text-xs text-white" data-testid={`fee-rate-${field}-input`} />
      {val && <button type="button" className="btn-gold btn-sm" onClick={() => save(Number(val.replace(",", ".")))} data-testid={`fee-rate-${field}-save`}>{t("fees.save")}</button>}
      {src === "override" && <button type="button" className="text-xs font-semibold text-emerald-300 underline" onClick={() => save(null)} data-testid={`fee-rate-${field}-auto`}>{t("fees.backAuto")}</button>}
    </li>
  );
};

export const FeeChangeBanner = () => {
  const { t } = useI18n();
  const m = useFeeModel();
  if (!m?.recent_change) return null;
  const dismiss = async () => {
    await api.post("/admin/fees/dismiss").catch(() => {});
    loadFeeModel(true);
  };
  return (
    <div role="status" className="flex min-w-0 items-start gap-3 rounded-2xl border border-emerald-500/60 bg-emerald-950/40 p-4 text-sm text-emerald-100" data-testid="fee-change-banner">
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="min-w-0 flex-1 break-words">{t("fees.changed", { text: (m.recent_change.items || []).map((c) => `${t(`fees.f.${c.field}`)} ${c.old} → ${c.new}`).join(" · ") || m.recent_change.text })}</p>
      <button type="button" onClick={dismiss} aria-label={t("fees.dismiss")} className="shrink-0 rounded-full p-1 hover:bg-white/10" data-testid="fee-change-dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
};

export const FeeModelCard = () => {
  const { t, fmtDate, locale } = useI18n();
  const m = useFeeModel();
  const [busy, setBusy] = useState(false);
  if (!m) return null;
  const sync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/fees/sync");
      await loadFeeModel(true);
    } finally {
      setBusy(false);
    }
  };
  const last = m.last_sync;
  return (
    <section className="card-carbon min-w-0 rounded-2xl p-5" data-testid="fee-model-card">
      <h2 className="break-words font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg">{t("fees.model")}</h2>
      <p className="mt-1 text-xs text-neutral-400">{t("fees.modelNote")}</p>
      <ul className="mt-3 min-w-0">{FIELDS.map((f) => <RateRow key={f} field={f} m={m} />)}</ul>
      <div className="mt-3 flex min-w-0 flex-wrap items-center gap-3 text-xs">
        <span className={`min-w-0 break-words ${last && !last.ok ? "text-red-300" : "text-neutral-400"}`} data-testid="fee-last-sync">
          {last ? (last.ok ? <>{t("fees.lastSync", { time: "" })}<bdi dir="ltr">{new Date(last.at).toLocaleString(locale)}</bdi></> : t("fees.syncFailed")) : "—"}
        </span>
        <button type="button" onClick={sync} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-600 px-3 py-1.5 font-semibold text-neutral-100 hover:border-[var(--gold)]" data-testid="fee-sync-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {t("fees.syncNow")}
        </button>
      </div>
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("fees.history")}</h3>
      {m.history?.length ? (
        <ul className="mt-2 space-y-1 text-xs text-neutral-300" data-testid="fee-history">
          {m.history.map((h) => <li key={`${h.at}-${h.field}`} className="break-words">{fmtDate(h.at)} · {t(`fees.f.${h.field}`)} : {h.old} → {h.new} ({h.samples})</li>)}
        </ul>
      ) : <p className="mt-1 text-xs text-neutral-500" data-testid="fee-history-empty">{t("fees.noHistory")}</p>}
    </section>
  );
};
